class MainSearch extends SearchForm {
  constructor() {
    super();
    
    // key fix: Prevent running logic when element is created in memory (e.g. by load-more.js parsing HTML)
    // This prevents the constructor from finding and hiding the live product grid
    if (!this.isConnected) return;

    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.setupEventListeners();
    // Filter products with tags containing special characters on page load
    if (window.location.pathname.includes('/search')) {
      // Check if search term has special characters first (fast, synchronous check)
      const urlParams = new URLSearchParams(window.location.search);
      const searchTerm = urlParams.get('q');
      const specialCharPattern = /[_:]/;
      
      if (searchTerm && specialCharPattern.test(searchTerm)) {
        // If search term has special chars, hide products immediately
        const productGrid = document.querySelector('.products-on-page');
        if (productGrid) {
          productGrid.style.opacity = '0';
          productGrid.style.visibility = 'hidden';
        }
      }
      
      this.filterProductsByTags().then(() => {
        // Show products after filtering (if they weren't all removed)
        const productGrid = document.querySelector('.products-on-page');
        if (productGrid && productGrid.children.length > 0) {
          productGrid.classList.add('filtered');
          productGrid.style.opacity = '1';
          productGrid.style.visibility = 'visible';
        }
      });
    }
  }

  setupEventListeners() {
    let allSearchForms = [];
    this.allSearchInputs.forEach((input) => allSearchForms.push(input.form));
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    if (allSearchForms.length < 2) return;
    allSearchForms.forEach((form) => form.addEventListener('reset', this.onFormReset.bind(this)));
    this.allSearchInputs.forEach((input) => input.addEventListener('input', this.onInput.bind(this)));
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen) {
      this.scrollIntoView({ behavior: 'smooth' });
    }
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) {
        input.value = value;
      }
    });
  }

  /**
   * Filters out products that have tags or synonyms containing special characters (_, :)
   * Hyphens (-) are allowed for SKU matching
   * Only alphanumeric tags and synonyms (with hyphens) are considered searchable
   */
  async filterProductsByTags() {
    // Get search term from URL
    const urlParams = new URLSearchParams(window.location.search);
    const searchTerm = urlParams.get('q');
    if (!searchTerm || !searchTerm.trim()) {
      return;
    }

    // Regex pattern to match tags/synonyms containing special characters: _, :
    // Hyphens (-) are excluded from this pattern to allow SKU matching
    const specialCharPattern = /[_:]/;
    const normalizedSearchTerm = searchTerm.toLowerCase().trim();
    
    // Hide products initially to prevent flash of unfiltered content
    const productGrid = document.querySelector('.products-on-page');
    if (productGrid) {
      productGrid.style.opacity = '0';
      productGrid.style.visibility = 'hidden';
    }
    
    // If search term itself contains special characters (_, :), exclude all products
    // because tags/synonyms with special characters should not be searchable
    if (specialCharPattern.test(searchTerm)) {
      const productItems = Array.from(document.querySelectorAll('.products-on-page li'));
      if (productItems.length > 0) {
        // Remove all products
        productItems.forEach(item => item.remove());
        
        // Show "No results found" message
        const productGridContainer = document.querySelector('#product-grid');
        const productsList = document.querySelector('.products-on-page');
        
        if (productGridContainer && productsList) {
          const noResultsDiv = document.createElement('div');
          noResultsDiv.className = 'template-search__no-results';
          noResultsDiv.innerHTML = `
            <div class="title-wrapper">
              <p role="status" class="title-wrapper--status">
                No results found for "${searchTerm}". Check the spelling or use a different word or phrase.
              </p>
            </div>
          `;
          productsList.replaceWith(noResultsDiv);
        }
        
        // Hide filters and load more button
        const facetsWrapper = document.querySelector('.facets-wrapper, #main-search-filters');
        const facetFiltersForm = document.querySelector('facet-filters-form, .facets-vertical-sort');
        const facetsVertical = document.querySelector('.facets-vertical');
        const loadMoreButton = document.querySelector('.load-more-button');
        
        if (facetsWrapper) facetsWrapper.style.display = 'none';
        if (facetFiltersForm) facetFiltersForm.style.display = 'none';
        if (facetsVertical) facetsVertical.style.display = 'none';
        if (loadMoreButton) loadMoreButton.style.display = 'none';
        
        // Update results count
        const resultsCountElement = document.querySelector('[role="status"]');
        if (resultsCountElement) {
          resultsCountElement.textContent = `No results found for "${searchTerm}". Check the spelling or use a different word or phrase.`;
        }
      }
      
      return Promise.resolve();
    }

    // Find all product items in search results
    const productItems = Array.from(document.querySelectorAll('.products-on-page li'));
    if (productItems.length === 0) {
      return;
    }

    // Extract product handles from product links
    const productData = productItems.map(item => {
      const link = item.querySelector('a[href*="/products/"]');
      if (!link || !link.href) return null;
      const match = link.href.match(/\/products\/([^\/\?]+)/);
      const handle = match ? match[1] : null;
      return handle ? { handle, element: item } : null;
    }).filter(data => data !== null);

    if (productData.length === 0) {
      return;
    }

    // Fetch product tags for each product
    const productTagsPromises = productData.map(async ({ handle, element }) => {
      try {
        const response = await fetch(`/products/${handle}.json`);
        if (!response.ok) {
          return { handle, element, tags: [], shouldExclude: false };
        }
        const productJson = await response.json();
        const product = productJson.product;
        if (!product) {
          return { handle, element, tags: [], shouldExclude: false };
        }

        // Handle tags - Shopify returns tags as comma-separated string or array
        let tags = [];
        if (product.tags) {
          if (Array.isArray(product.tags)) {
            tags = product.tags;
          } else if (typeof product.tags === 'string') {
            tags = product.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
          }
        }
        const variants = product.variants || [];
        const metafields = product.metafields || [];

        // Extract synonyms from metafields
        const synonyms = [];
        metafields.forEach(metafield => {
          if (metafield.key && metafield.key.toLowerCase().includes('synonym')) {
            const value = metafield.value || '';
            if (typeof value === 'string' && value.trim()) {
              synonyms.push(...value.split(',').map(s => s.trim()).filter(s => s));
            }
          }
        });

        // Separate tags/synonyms into clean and invalid
        const cleanTags = [];
        const invalidTags = [];

        [...tags, ...synonyms].forEach(term => {
          if (specialCharPattern.test(term)) {
            invalidTags.push(term);
          } else {
            cleanTags.push(term);
          }
        });

        // Check if search term matches invalid tags/synonyms
        const matchingInvalidTerms = invalidTags.filter(term => {
          const normalizedTerm = term.toLowerCase();
          // Check if the search term is contained in the tag/synonym, or if tag contains special chars and matches
          // Also check for exact match or if search term (without special chars) matches tag content
          const searchTermWithoutSpecialChars = normalizedSearchTerm.replace(/[_:]/g, '');
          const tagWithoutSpecialChars = normalizedTerm.replace(/[_:]/g, '');
          
          // Match if: exact match, search term in tag, or tag content matches search term content
          return normalizedTerm === normalizedSearchTerm || 
                 normalizedTerm.includes(normalizedSearchTerm) ||
                 (searchTermWithoutSpecialChars && tagWithoutSpecialChars.includes(searchTermWithoutSpecialChars));
        });

        // Check if search term matches clean tags/synonyms
        const matchingCleanTags = cleanTags.filter(term => {
          const normalizedTerm = term.toLowerCase();
          return normalizedTerm.includes(normalizedSearchTerm);
        });

        // Get product title and other fields for comparison
        const titleElement = element.querySelector('.card__heading a, .card-information__text a');
        const title = titleElement ? titleElement.textContent.trim().toLowerCase() : '';
        const matchesOnTitle = title.includes(normalizedSearchTerm);
        const matchesOnSku = variants.some(variant =>
          (variant.sku || '').toLowerCase().includes(normalizedSearchTerm)
        );

        // Only exclude if product EXCLUSIVELY matches on invalid tags (with special characters: _ or :)
        // Keep product if it matches on ANY clean field (title, SKU, clean tags)
        // This ensures users see products that match their search term in legitimate fields
        const matchesOnAnyCleanField = matchesOnTitle || matchesOnSku || matchingCleanTags.length > 0;
        const shouldExclude = matchingInvalidTerms.length > 0 && !matchesOnAnyCleanField;

        return { handle, element, tags, shouldExclude };
      } catch (error) {
        // If fetch fails, keep the product (fail open)
        return { handle, element, tags: [], shouldExclude: false };
      }
    });

    // Wait for all product data to be fetched
    const productsWithTags = await Promise.all(productTagsPromises);

    // Filter out products that should be excluded
    const validProducts = productsWithTags.filter(({ shouldExclude }) => !shouldExclude);
    const excludedProducts = productsWithTags.filter(({ shouldExclude }) => shouldExclude);

    // Remove excluded products from DOM
    excludedProducts.forEach(({ element }) => {
      element.remove();
    });

    // If no products remain after filtering, show "No results found" message
    if (validProducts.length === 0 && productItems.length > 0) {
      const productGrid = document.querySelector('#product-grid');
      const productsList = document.querySelector('.products-on-page');
      
      if (productGrid && productsList) {
        // Create "No results found" message
        const noResultsDiv = document.createElement('div');
        noResultsDiv.className = 'template-search__no-results';
        noResultsDiv.innerHTML = `
          <div class="title-wrapper">
            <p role="status" class="title-wrapper--status">
              No results found for "${searchTerm}". Check the spelling or use a different word or phrase.
            </p>
          </div>
        `;
        
        // Replace the products list with the message
        productsList.replaceWith(noResultsDiv);
      }

      // Hide filters when there are no results
      const facetsWrapper = document.querySelector('.facets-wrapper, #main-search-filters');
      const facetFiltersForm = document.querySelector('facet-filters-form, .facets-vertical-sort');
      const facetsVertical = document.querySelector('.facets-vertical');
      
      if (facetsWrapper) {
        facetsWrapper.style.display = 'none';
      }
      if (facetFiltersForm) {
        facetFiltersForm.style.display = 'none';
      }
      if (facetsVertical) {
        facetsVertical.style.display = 'none';
      }
    } else {
      // Show filters if products exist
      const facetsWrapper = document.querySelector('.facets-wrapper, #main-search-filters');
      const facetFiltersForm = document.querySelector('facet-filters-form, .facets-vertical-sort');
      const facetsVertical = document.querySelector('.facets-vertical');
      
      if (facetsWrapper) {
        facetsWrapper.style.display = '';
      }
      if (facetFiltersForm) {
        facetFiltersForm.style.display = '';
      }
      if (facetsVertical) {
        facetsVertical.style.display = '';
      }
    }

    // Hide "Load more" button if there are no products
    setTimeout(() => {
      const loadMoreButton = document.querySelector('.load-more-button');
      if (loadMoreButton) {
        const remainingProducts = document.querySelectorAll('.products-on-page li, #product-grid li.grid__item');
        if (remainingProducts.length === 0) {
          loadMoreButton.style.display = 'none';
        } else {
          loadMoreButton.style.display = '';
        }
      }
    }, 100);

    // Update results count if element exists
    const resultsCountElement = document.querySelector('[role="status"]');
    if (resultsCountElement && excludedProducts.length > 0) {
      const currentText = resultsCountElement.textContent;
      const countMatch = currentText.match(/(\d+)/);
      if (countMatch) {
        const currentCount = parseInt(countMatch[1]);
        const newCount = currentCount - excludedProducts.length;
        if (newCount > 0) {
          resultsCountElement.textContent = currentText.replace(/\d+/, newCount);
        } else {
          // Update to show no results message
          resultsCountElement.textContent = `No results found for "${searchTerm}". Check the spelling or use a different word or phrase.`;
        }
      }
    }

    // Show products after filtering completes (if any remain)
    const remainingProductGrid = document.querySelector('.products-on-page');
    if (remainingProductGrid) {
      if (remainingProductGrid.children.length > 0) {
        remainingProductGrid.classList.add('filtered');
        remainingProductGrid.style.opacity = '1';
        remainingProductGrid.style.visibility = 'visible';
      }
      // If no products, keep hidden (no results message will be shown)
    }

    // Return promise for chaining
    return Promise.resolve();
  }
}

customElements.define('main-search', MainSearch);
